/**
 * Helper for accessing portal/DS annotation API
 * @module
 */
import * as _ from 'lodash'
import { post, get } from 'tools/request'
//import * as sample from '../../test/unit/components/extract/pedersentr-2005-ds-annotation.json'
import { ETerm } from 'services/extract/util'
import { DocSearchTerm, Sense, splitPhrase } from 'util/terms'
import { inferPICO } from 'util/semanticTypes'

const cached = {
	//Q3jRyMLc0tQEAA: sample
}

// Followup/Follow-up
// http://vocab.drevidence.com/term/docdata#48438926
// http://purl.bioontology.org/ontology/SNOMEDCT/308273005
// http://purl.bioontology.org/ontology/NCI/C1522577
export const FOLLOW_UP_SENSES = ['SUFzuS', 'SUCP1B']
// Population Group
// http://purl.bioontology.org/ontology/MESH/D044382
// http://purl.bioontology.org/ontology/SNOMEDCT/389109008
// http://purl.bioontology.org/ontology/NCI/C1257890
// Patients
// http://purl.bioontology.org/ontology/MESH/D010361
// http://vocab.drevidence.com/term/docdata#1365715
// http://vocab.drevidence.com/term/docdata#73373744
// http://purl.bioontology.org/ontology/SNOMEDCT/116154003
// http://purl.bioontology.org/ontology/NCI/C0030705
// Group
// http://purl.bioontology.org/ontology/MESH/D044382
// http://vocab.drevidence.com/term/docdata#17252265
// http://vocab.drevidence.com/term/docdata#394701
// http://purl.bioontology.org/ontology/SNOMEDCT/389109008
// http://purl.bioontology.org/ontology/SNOMEDCT/255504006
// http://purl.bioontology.org/ontology/NCI/C1705429
// http://purl.bioontology.org/ontology/NCI/C1522242
// http://purl.bioontology.org/ontology/NCI/C1257890
export const GROUP_SENSES = [
	'SUJ-y', // Population Group
	'SUCLv', // Patients
	/*'SUJ-y',*/ 'SUBrXC', 'SUGgXW', 'SUFzpD' // Group
]

export const PLACEBO_SENSE = 'SUGeLS' as Sense
export const isPlacebo = itvn => itvn.sense == PLACEBO_SENSE || /placebo/i.test(itvn.label)

export const RXNORM_ONTOLOGY_RE = /^http:\/\/purl.bioontology.org\/ontology\/RXNORM\//i

export const termToString = t => `[sense: ${t.sense} uri: ${t.uri}` +
							     (t.tags?.length ? ` tags: [${t.tags.join(', ')}]` : '') +
							      ` alts: (${t.alts.join(', ')})]`

export function termsRelated(term: DocSearchTerm, terms: DocSearchTerm[]) {
	return (term.sense && terms.some(t => t.sense == term.sense)) ||
	        (term.uri && terms.some(t => term.uri == t.uri))
}

export function phraseRelated(annotatedPhrase: ETerm, terms: DocSearchTerm[]) {
	if (!annotatedPhrase) return false
	return _.some(annotatedPhrase.senses, sense => {
		const primary = splitPhrase(sense)[0]
		return terms.some(t => t.sense == primary)
	})
}

type Range = [number, number]
type Selection = Range[]
type Tag = 'I' | 'O' | 'C'

interface DSAnnotation {
	phrase: DocSearchTerm[]
	selection: Range[]
	tags?: Tag[]
	tuis?: string[]
}

export type DSNodeAnnotations = { annotations: DSAnnotation[] }
type DSAnnotateResponse = DSNodeAnnotations[]

export interface DocSearchResult {
	title: DSNodeAnnotations
	abstract: DSNodeAnnotations
}

export const entireSelection = (selection: Selection): Range => {
	// from the start of the first sub-span
	const [start] = selection[0]
	// to the end of the last
	const [lastStart, lastLen] = selection[selection.length - 1]
	return [start, lastStart + lastLen - 1]
}

export const annotationContext = (a, text, context = 10) => {
	const [start, end] = entireSelection(a.selection)
	const before = Math.max(start - context, 0)
	const after = Math.min(text.length, end + 1 + context)
	const atext = text.slice(start, end + 1)
	return {
		text: atext,
		context: text.slice(before, start) + '[' + atext + ']' + text.slice(end + 1, after)
	}
}

export const atos = (a, annotationResults, text, context = 10) => {
	const atext = annotationContext(a, text).context
	const measures = a['measure-ids']
	let result = atext + (a.tags ? ' (' + a.tags.join(',') : '') + ')'
	if (measures && measures.length) {
		result += ' measures:\n' + annotationResults.measures.filter(m => measures.includes(m['measure-id'])).map(m =>
			`${m.comparator} ${m.amount} ${m.unit}`.trim()
		).join('\n')
	}
	return result
}

const PICO_CODE = {
	int: 'I',
	out: 'O',
	char: 'C'
} as const

export default class DocSearch {
	/**
	 * returns sorted docsearch annotations
	 */
	static async findAnnotations(title, text, id?): Promise<DocSearchResult> {
		const docs = [title || '', text || '']
		const nodes = _.compact(docs)
		let results: DSAnnotateResponse = [{ annotations: [] }, { annotations: [] }]
		if (nodes.length) {
			const response = cached[id] || await post<any>('/portal/annotate/search', {
				data: {
					annotate: nodes
				}
			})
			results = docs.map((input, i) => !!input ? response.shift() : results[i])
		}
		const terms = new Map<string, DocSearchTerm>()
		for (const node of results) {
			// make sure we have loaded and normalized all referenced terms
			for (const annotation of node.annotations) {
				// if no PICO has been inferred, try to infer it ourselves
				if (!annotation.tags?.length) {
					const inferred = PICO_CODE[inferPICO(annotation.tuis)]
					if (inferred) {
						(annotation.tags || (annotation.tags = [])).push(inferred)
					}
				}
				for (let i = 0; i < annotation.phrase.length; i++) {
					const term = annotation.phrase[i]
					if (!term.uri) {
						console.warn('Annotation term without uri: ', term)
						continue
					}
					const t = terms.get(term.uri)
					if (!t) {
						terms.set(term.uri, term)
					} else {
						annotation.phrase[i] = t
					}
				}
			}
		}
		return {
			title: results[0],
			abstract: results[1]
		}
	}
}